import { RetryLink } from '@apollo/client/link/retry';

import { Loggable } from '@rbi-ctg/frontend';
import { LaunchDarklyFlag, staticLaunchDarklyFlags } from 'state/launchdarkly';
import logger from 'utils/logger';

import { isChaosModeEnabled } from './chaos-mode';

const DEFAULT_MAX_SANITY_API_RETRIES = 10;
const getMaxSanityApiRetries = () =>
  staticLaunchDarklyFlags.instance[LaunchDarklyFlag.MAX_SANITY_API_RETRIES] ||
  DEFAULT_MAX_SANITY_API_RETRIES;

export const sanityBackendRetryLink = new RetryLink({
  attempts: count => count <= getMaxSanityApiRetries(),
  delay: { max: 8000 },
});

const DEFAULT_MAX_RBI_API_RETRIES = 5;

const getMaxRbiApiRetries = () =>
  staticLaunchDarklyFlags.instance[LaunchDarklyFlag.MAX_RBI_API_RETRIES] ||
  DEFAULT_MAX_RBI_API_RETRIES;

// Only put mutations in this list you are sure are safe to retry aka NOT "CreateOrder" etc.
const MUTATION_ALLOW_LIST = ['PriceOrder', 'UpdateMe', 'CreateOTP', 'ValidateOTP'];

const logRetry = (logBody: Loggable | Array<Loggable>) => {
  // we want to see this in datadog in prod for now
  logger.info(logBody);
  if (!__DEV__ && isChaosModeEnabled) {
    // we want to see these in the console in prod mode if chaosmode is enabled to help understand whats going on
    // eslint-disable-next-line dot-notation
    global['console']['warn'](logBody);
  }
};

export const rbiBackendRetryLink = new RetryLink({
  attempts: (count, operation, error) => {
    try {
      const operationType = (operation?.query?.definitions?.[0] as any)?.operation as
        | 'mutation'
        | 'query';

      // be nicer to our RBI backend to avoid a flood of retries
      if (count > getMaxRbiApiRetries()) {
        if (count > getMaxRbiApiRetries() + 1) {
          // don't spam logs with retries over the limit
          return false;
        }

        const operationName = operation?.operationName;
        logRetry({
          message: `[RetryLog] No Retry - Exceeded max retries for ${operationType} ${operationName} at attempt ${count}/${getMaxRbiApiRetries()}`,
          error,
          operationName,
          operationType,
          isRetrying: false,
          isRetryLog: true,
          attemptNumber: count,
        });
        return false;
      }

      // only retry mutations in our allow list - cause like we don't want to make duplicate orders (etc)!
      if (operationType === 'mutation') {
        const operationName = operation?.operationName;
        const shouldRetry = MUTATION_ALLOW_LIST.includes(operationName);
        logRetry({
          message: shouldRetry
            ? `[RetryLog] Retrying mutation ${operationName} attempt ${count}/${getMaxRbiApiRetries()}`
            : `[RetryLog] No Retry - Mutation ${operationName} not in allow list`,
          error,
          operationName,
          operationType,
          isRetrying: shouldRetry,
          isRetryLog: true,
          attemptNumber: count,
        });
        return shouldRetry;
      }

      // queries don't mutate data so are safe to retry under all circumstances
      if (operationType === 'query') {
        const operationName = operation?.operationName;
        logRetry({
          message: `[RetryLog] Retrying query ${operationName} attempt ${count}/${getMaxRbiApiRetries()}`,
          error,
          operationName,
          operationType,
          isRetrying: true,
          isRetryLog: true,
          attemptNumber: count,
        });

        return true;
      }
    } catch (error) {}

    // something unexpected is happening - don't retry to be safe
    return false;
  },
  delay: { max: 8000 },
});
